<template>
    <v-container fluid grid-list-lg>
        <v-progress-linear indeterminate v-if="loading"/>
        <v-layout v-else>
            <v-flex md4>
                <div class="headline pb-2">Tilbud</div>
                <v-card class="pa-3">
                    <v-text-field label="Overskrift" class="mb-2" v-model="offer.description" outlined hide-details/>

                    <v-layout class="mt-2">
                        <v-flex sm6>
                            <v-text-field label="Kundenavn" v-model="offer.customer_name" outlined hide-details/>
                        </v-flex>
                        <v-flex sm6>
                            <ProjectSelect label="Knytt mot prosjekt" v-model="offer.project" outlined hide-details/>
                        </v-flex>
                    </v-layout>
                    <v-layout class="mt-2">
                        <v-flex sm6>
                            <DatePickerField label="Tilbudsdato" v-model="offer.date"/>
                        </v-flex>
                        <v-flex sm6>
                            <DatePickerField label="Gyldig t.o.m." v-model="offer.valid_until"/>
                        </v-flex>
                    </v-layout>
                    <v-layout>
                        <v-flex sm6>
                            <v-text-field outlined hide-details v-model="offer.customer_ref" class="pb-3"
                                          label="Deres referanse"/>
                            <v-text-field outlined label="Epost" v-model="offer.customer_email" persistent-hint
                                          hint="Adressen tilbudet sendes til."/>
                        </v-flex>
                        <v-flex sm6>
                            <ProjectManagerSelect label="Vår referanse" v-model="offer.our_ref"/>
                        </v-flex>
                    </v-layout>
                </v-card>
            </v-flex>
            <v-flex>
                <div class="headline pb-2">Tilbudstekst</div>
                <v-textarea solo v-model="offer.intro" hide-details class="pb-3"/>

                <div class="headline pb-2">Tilbudslinjer</div>
                <v-simple-table class="elevation-1" dense>
                    <template v-slot:default>
                        <thead>
                        <tr>
                            <th class="text-center pr-5" style="width: 84px">Tekst</th>
                            <th colspan="4">Tilbudslinje</th>
                            <th class="text-right">Sum</th>
                            <th>&nbsp;</th>
                        </tr>
                        </thead>
                        <tbody>
                        <OfferRow v-for="line in offer.lines" :value="line" @addBefore="addLineBefore"
                                  @remove="removeLine" :key="line.key"/>
                        </tbody>
                        <tfoot>
                        <tr class="offer-row">
                            <td colspan="5">
                            </td>
                            <td class="text-right">
                                {{ formatMoney(sum) }}
                            </td>
                            <td>
                                <v-btn icon @click="addLineLast" color="green">
                                    <v-icon>add</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                        </tfoot>
                    </template>
                </v-simple-table>
                <div class="pt-3">
                    <v-btn :loading="saving" color="green" dark @click="save">Lagre tilbud</v-btn>
                    <v-btn :loading="downloading" color="green" outlined class="ml-3" @click="save('download')">
                        Lagre og last ned PDF
                    </v-btn>
                    <v-btn :loading="sending" color="blue" outlined class="ml-3" @click="save('email')" :disabled="!offer.customer_email">
                        Lagre og send på epost
                    </v-btn>
                </div>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
    import dayjs from "dayjs";
    import {formatMoney} from "../helpers";
    import OfferRow from "../components/helpers/OfferRow";
    import DatePickerField from "../components/helpers/DatePickerField";
    import ProjectManagerSelect from "../components/helpers/ProjectManagerSelect";
    import ProjectSelect from "../components/helpers/ProjectSelect";

    let newEmptyLine = (type, sort) => {
        if (type === 'text') {
            return {
                key: Math.random(),
                type: 'text',
                text: '',
                sort: sort
            };
        }
        return {
            key: Math.random(),
            type: 'product',
            text: '',
            count: 1,
            unit: 'stk',
            price: 0,
            discount: 0,
            sum: 0,
            sort: sort
        }
    };

    const ORDER_START = 10000;

    function newEmptyOffer() {
        return {
            project: null,
            customer_name: '',
            description: '',
            intro: '',
            date: dayjs().format('YYYY-MM-DD'),
            valid_until: dayjs().add(60, 'days').format('YYYY-MM-DD'),
            customer_ref: '',
            customer_email: '',
            our_ref: null,
            lines: [
                newEmptyLine('product', ORDER_START)
            ]
        };
    }

    export default {
        components: {
            OfferRow,
            DatePickerField,
            ProjectManagerSelect,
            ProjectSelect
        },

        data() {
            return {
                loading: false,
                offer: null,

                saving: false,
                downloading: false,
                sending: false
            }
        },

        created() {
            if (this.offerId) {
                this.loadData();
            } else {
                this.offer = newEmptyOffer();
            }
        },

        methods: {
            formatMoney,
            loadData() {
                this.loading = true;
                this.$http.get(`offers/${this.offerId}`).then(response => {
                    this.offer = response.data;
                }).finally(() => {
                    this.loading = false;
                })
            },
            addLineBefore(after) {
                let indexBefore = this.offer.lines.indexOf(after);
                let sort = ORDER_START;
                if (indexBefore === 0) {
                    // Add bafore first line.
                    sort = this.offer.lines[indexBefore].sort - 1;
                } else if (indexBefore > 0) {
                    // Add between lines first line.
                    sort = after.sort + 1;
                    let before = this.offer.lines[indexBefore - 1] || null;
                    if (after) {
                        sort = (after.sort + before.sort) / 2;
                    }
                }
                this.addLine(sort);
            },
            addLineLast() {
                this.addLine(this.offer.lines[this.offer.lines.length - 1].sort + 1);
            },
            addLine(insertOrder) {
                let lines = [
                    ...this.offer.lines,
                    newEmptyLine('product', insertOrder)
                ].sort((a, b) => a.sort - b.sort);

                this.offer.lines = lines;
            },
            removeLine(line) {
                // Always keep one line.
                if (this.offer.lines.length <= 1) {
                    return;
                }

                let idx = this.offer.lines.indexOf(line);
                this.offer.lines.splice(idx, 1);
            },

            save(action = null) {
                let request;
                if (this.offer.id) request = this.$http.put(`offers/${this.offer.id}/`, this.offer);
                else request = this.$http.post(`offers/`, this.offer);

                this.saving = true;
                request.then(response => {
                    this.offer = response.data;

                    if (action === 'download') {
                        this.downloading = true;
                        this.$http.get(`offers/${this.offer.id}/pdf/`, {responseType: 'blob'}).then(response => {
                            console.log(response);
                            let fileURL = window.URL.createObjectURL(new Blob([response.data]));
                            let fileLink = document.createElement('a');
                            fileLink.href = fileURL;

                            fileLink.setAttribute('download', `tilbud-${this.offer.id}.pdf`);
                            document.body.appendChild(fileLink);
                            fileLink.click();
                            this.$root.addMessage('PDF lastet ned.', 'green');
                        }).catch(() => {
                            this.$root.addMessage('Kunne ikke generere PDF.', 'red');
                        }).finally(() => {
                            this.downloading = false;
                        });
                    } else if (action === 'email') {
                        let ok = confirm('Vil du sende dette tilbudet på epost til kunden?');
                        if (!ok) return;

                        this.sending = true;
                        this.$http.post(`offers/${this.offer.id}/email/`).then(response => {
                            this.$root.addMessage('Tilbudet er sendt på epost.', 'green');
                        }).catch(() => {
                            this.$root.addMessage('Feil under sending av tilbud på epost.', 'red');
                        }).finally(() => {
                            this.sending = false;
                        });
                    } else {
                        this.$root.addMessage('Tilbud lagret.', 'green');
                    }
                }).catch(() => {
                    this.$root.addMessage('Feil under lagring. Sjekk at alle nødvendige felter er fylt ut.', 'red');
                }).finally(() => {
                    this.saving = false;
                });
            }
        },

        computed: {
            offerId() {
                return this.$route.params.id;
            },
            sum() {
                return this.offer.lines.reduce((acc, line) => {
                    return (parseFloat(line.sum) || 0) + acc
                }, 0).toFixed(2);
            }
        }
    }
</script>